/**
 * The notification card (SPEC `notifications` § The notification card; SPEC `New Post Watching`
 * § In-app card).
 *
 * One component, three faces — chosen by `notificationCardModel`, which is the only place the
 * choice is made:
 *
 *  - **viral** — a parameterized `FeedCard` under a **purple** strip. Clicking opens the shared
 *    preview popup on the route the card is already on.
 *  - **new post, rich** — the same `FeedCard` under a **green** strip. Since the watch→analytics
 *    bridge the alert points at a real `posts` row, so the body is the live card, metric chips and
 *    all (≈0 right after detection, healing as data lands). Clicking crosses to `/watch?post=<id>`
 *    — the Posts-to-Comment feed with the popup open over it.
 *  - **new post, degraded** — no resolvable post (a legacy row, or the ingest bridge threw): the
 *    frozen payload rendered by hand under the same green strip, with no media and no chips, and a
 *    whole-card click falling back to LinkedIn. Such a row self-heals server-side the moment the
 *    post reaches analytics, so this face is temporary by design.
 *
 * The alert chrome is the same on all three: the strip across the card top (what fired on the left,
 * the fire time on the right — every string the server's, rendered verbatim, §12.5), the media cap
 * at 384px behind a measured fade, and a primary **Mark As Read** ahead of the icon-only actions on
 * unread cards only. A read card is muted, drops the button and states its read date quietly.
 *
 * Both green faces carry one extra footer link, **Show posts**, straight after Mark As Read: it
 * opens the author's LinkedIn profile rather than the fresh permalink. Viral cards do not.
 *
 * Opening the preview popup does *not* mark anything read — #49 is the only read trigger.
 */

import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { watchDeepLinkPath, type NotificationDTO } from '@lw/shared';
import { Avatar, ExternalLinkIcon, IconAction, cx } from '../../components';
import { viewPath } from '../../app/routes';
import { isPlainCardClick } from '../../lib/plainClick';
import { FeedCard } from '../view/feed/FeedCard';
import { notificationCardModel, type NewPostDegradedPlan } from './notificationCardModel';

/** SPEC § media cap. */
export const NOTIFICATION_MEDIA_CAP_PX = 384;

export type NotificationCardProps = {
  /** Either kind: the plan below decides which body renders. */
  notification: NotificationDTO;
  mediaEnabled: boolean;
  /** Opens the shared popup **on the current route** — the viral card's click. */
  onOpen: (postId: number) => void;
  onMarkRead: (id: number) => void;
  /** True while the mark-as-read request is in flight — the card animates out. */
  dismissing: boolean;
};

export function NotificationCard({
  notification,
  mediaEnabled,
  onOpen,
  onMarkRead,
  dismissing,
}: NotificationCardProps) {
  const navigate = useNavigate();
  const plan = notificationCardModel(notification);

  const banner = (
    <div className={cx('notif-strip', plan.accentClass)}>
      <span className="notif-strip__reason">{plan.bannerText}</span>
      <span className="notif-strip__when">{notification.firedAtDisplay}</span>
    </div>
  );

  const markAsRead = notification.read ? null : (
    <button
      type="button"
      className="btn-primary btn-primary--sm"
      onClick={() => onMarkRead(notification.id)}
      disabled={dismissing}
    >
      Mark As Read
    </button>
  );

  // Green faces only (`showPostsUrl` exists on neither the viral plan nor `PlanChrome`): the alert
  // sends the user to the author's profile, never to a post published minutes ago. A link, not a
  // mutation — so it survives on a read card, where Mark As Read is gone.
  const footerLead =
    plan.kind === 'viral' ? (
      markAsRead
    ) : (
      <>
        {markAsRead}
        {plan.showPostsUrl === null ? null : (
          <IconAction
            label="Show posts"
            href={plan.showPostsUrl}
            icon={<ExternalLinkIcon size={14} />}
            showLabel
          />
        )}
      </>
    );

  const footerNote =
    notification.read && notification.readAtDisplay !== null ? (
      <span className="faint notif-read-at">Read {notification.readAtDisplay}</span>
    ) : null;

  const className = cx(
    'post-card--notif',
    notification.read && 'post-card--notif-read',
    dismissing && 'post-card--dismissing',
  );

  if (plan.kind === 'new-degraded') {
    return (
      <DegradedNewPostCard
        plan={plan}
        profileId={notification.profileId}
        banner={banner}
        footerLead={footerLead}
        footerNote={footerNote}
        className={className}
      />
    );
  }

  return (
    <FeedCard
      card={plan.card}
      mediaEnabled={mediaEnabled}
      // `FeedCard` hands back the id of whatever was clicked — the card itself, or a repost's
      // embedded original. A viral alert opens it in place; a green one crosses to `/watch`, where
      // the same popup is the Posts-to-Comment deep link.
      onOpen={(postId) => {
        if (plan.click.type !== 'route') {
          onOpen(postId);
          return;
        }
        void navigate(postId === plan.card.id ? plan.click.to : watchDeepLinkPath(postId));
      }}
      banner={banner}
      footerLead={footerLead}
      footerNote={footerNote}
      compactActions
      mediaCapPx={NOTIFICATION_MEDIA_CAP_PX}
      className={className}
    />
  );
}

type DegradedNewPostCardProps = {
  plan: NewPostDegradedPlan;
  /** The account the alert is about — the card links to it exactly as a feed card does. */
  profileId: number;
  banner: ReactNode;
  footerLead: ReactNode;
  footerNote: ReactNode;
  className: string;
};

/**
 * The fallback body: everything here was frozen at fire time, because there is no post row to build
 * a live card from. No avatar in the payload either — the initials tile is the honest stand-in.
 */
function DegradedNewPostCard({
  plan,
  profileId,
  banner,
  footerLead,
  footerNote,
  className,
}: DegradedNewPostCardProps) {
  const post = plan.newPost;
  const authorPath = viewPath('profile', profileId);
  // The only way out of a degraded card. Without it the card is inert — nothing to open, so no
  // pointer, no tab stop, no lie.
  const url = plan.click.type === 'external' ? plan.click.url : null;

  const open = () => {
    if (url !== null) window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <article
      className={cx('card post-card', url !== null && 'post-card--clickable', className)}
      tabIndex={url === null ? undefined : 0}
      onClick={
        url === null
          ? undefined
          : (event: MouseEvent<HTMLElement>) => {
              if (isPlainCardClick(event)) open();
            }
      }
      onKeyDown={
        url === null
          ? undefined
          : (event: KeyboardEvent<HTMLElement>) => {
              // Enter on the card itself; Space keeps scrolling, and a key pressed on a focused
              // link or button inside belongs to that control.
              if (event.key === 'Enter' && event.target === event.currentTarget) open();
            }
      }
    >
      {banner}

      <header className="post-card__head">
        <Link to={authorPath} tabIndex={-1} aria-hidden="true">
          <Avatar media={null} name={post.authorName} mediaEnabled={false} kind={post.authorKind} />
        </Link>
        <div className="post-card__author">
          <Link className="post-card__name" to={authorPath}>
            {post.authorName}
          </Link>
          <span className="faint">{post.authorSlug}</span>
          <span className="post-card__headline">{post.publishedAtDisplay}</span>
        </div>
        <div className="post-card__flags">
          <span className="tag">{post.postTypeDisplay}</span>
        </div>
      </header>

      {post.preview === null ? null : <p className="notif-degraded__preview">{post.preview}</p>}

      <footer className="post-card__foot">
        {footerLead}
        {url === null ? null : (
          <IconAction label="Open on LinkedIn" href={url} icon={<ExternalLinkIcon size={14} />} />
        )}
        {footerNote}
      </footer>
    </article>
  );
}

export default NotificationCard;
